import React, { useEffect, useState } from 'react';
import { X, User as UserIcon, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost } from '../../lib/api';
import EmployeePicker from '../EmployeePicker';
import CompanyPicker from './CompanyPicker';
import { emailValidationError } from '../../lib/validators';

/**
 * Slide-in drawer for creating a full Contact without leaving the parent form.
 *
 * Props:
 *   open               boolean — drawer visibility
 *   onClose            () => void
 *   onCreated          (contactObj) => void — called after successful create OR reuse
 *   prefill            partial contact (e.g. { company_id, name })
 *   lockCompany        boolean — when true, the Company field is read-only
 */
export default function ContactFormDrawer({ open, onClose, onCreated, prefill = {}, lockCompany = false }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', designation: '', company_id: '',
    is_primary: false, tags: [], owner: '', linkedin: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null); // { message, existing_contact, existing_company_name }

  useEffect(() => {
    if (open) {
      setDuplicateInfo(null);
      // Reset to a clean blank base, then apply prefill on top.
      // Using spread (...f) here caused LinkedIn / notes / tags / is_primary
      // to carry over from the previous submission.
      setForm({
        name: prefill.name || '',
        email: prefill.email || '',
        phone: prefill.phone || '',
        designation: prefill.designation || '',
        company_id: prefill.company_id || '',
        is_primary: false,
        tags: [],
        owner: prefill.owner || '',
        linkedin: '',
        notes: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill.company_id, prefill.name]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const emailErr = emailValidationError(form.email);
    if (emailErr) { toast.error(emailErr); return; }
    setSaving(true);
    try {
      const res = await apiPost('/crm/contacts', form);
      const newContact = res.contact || res;
      toast.success(`Contact "${form.name}" created`);
      onCreated?.(newContact);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.existing_contact) {
        // Show reuse panel — don't toast, the panel explains everything
        setDuplicateInfo(detail);
      } else {
        toast.error(
          typeof detail === 'string' ? detail :
          detail?.message || err?.response?.data?.detail || err?.message || 'Failed to create contact'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const useExisting = () => {
    const c = duplicateInfo.existing_contact;
    toast.success(`Using existing contact: ${c.name}`);
    onCreated?.(c);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="contact-drawer">
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="flex-1 bg-black/40 cursor-default"
      />
      {/* Drawer */}
      <div className="w-full max-w-md h-full bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserIcon size={16} className="text-[#3b4872]" />
            <h3 className="font-display font-bold text-lg">New Contact</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
        </div>

        {/* Duplicate reuse panel — shown instead of the form when 409 fires */}
        {duplicateInfo ? (
          <div className="flex-1 flex flex-col p-5 gap-4" data-testid="contact-drawer-duplicate-panel">
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  {duplicateInfo.existing_contact.name} already exists
                  {duplicateInfo.existing_company_name && (
                    <span className="font-normal text-amber-700"> (currently linked to {duplicateInfo.existing_company_name})</span>
                  )}
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  That's fine — the same contact can be the Primary Contact on multiple deals across different companies, just like in HubSpot.
                </p>
              </div>
            </div>

            {/* Existing contact card */}
            <div className="border border-slate-200 rounded-lg p-4 space-y-1">
              <div className="flex items-center gap-2">
                <UserIcon size={15} className="text-[#3b4872]" />
                <span className="font-semibold text-sm">{duplicateInfo.existing_contact.name}</span>
              </div>
              {duplicateInfo.existing_contact.designation && (
                <p className="text-xs text-slate-500 ml-5">{duplicateInfo.existing_contact.designation}</p>
              )}
              <p className="text-xs text-slate-500 ml-5">{duplicateInfo.existing_contact.email}</p>
              {duplicateInfo.existing_contact.phone && (
                <p className="text-xs text-slate-500 ml-5">{duplicateInfo.existing_contact.phone}</p>
              )}
            </div>

            <div className="space-y-2">
              <button
                onClick={useExisting}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm text-sm font-bold transition-colors"
                data-testid="contact-drawer-use-existing"
              >
                <ArrowRight size={14} />
                Use this contact for this deal
              </button>
              <button
                onClick={() => setDuplicateInfo(null)}
                className="w-full text-xs px-4 py-2 rounded-sm border border-slate-300 text-slate-600 hover:bg-slate-50"
                data-testid="contact-drawer-edit-email"
              >
                Edit email and try again
              </button>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="contact-drawer-form">
              <div>
                <label className="label">Company {lockCompany && <span className="text-[10px] text-slate-500 ml-1">(locked)</span>}</label>
                {lockCompany ? (
                  <div className="border border-slate-200 rounded-sm px-3 py-2 bg-slate-50 text-sm">{prefill.company_name || '—'}</div>
                ) : (
                  <CompanyPicker value={form.company_id} onChange={(id) => setForm({ ...form, company_id: id })} />
                )}
              </div>
              <div>
                <label className="label">Name *</label>
                <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-drawer-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Email *</label>
                  <input
                    className={`input ${form.email && emailValidationError(form.email) ? 'border-red-400' : ''}`}
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-drawer-email"
                  />
                  {form.email && emailValidationError(form.email) && (
                    <p className="text-[10px] text-red-600 mt-1">{emailValidationError(form.email)}</p>
                  )}
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-drawer-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Designation</label>
                  <input className="input" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Project Manager" data-testid="contact-drawer-designation" />
                </div>
                <div>
                  <label className="label">LinkedIn</label>
                  <input className="input" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" />
                </div>
              </div>
              <div>
                <label className="label">Contact Owner</label>
                <EmployeePicker value={form.owner} onChange={(v) => setForm({ ...form, owner: v })} testid="contact-drawer-owner" />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <input type="checkbox" id="contact-primary" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} className="accent-[#3b4872]" />
                <label htmlFor="contact-primary" className="text-xs text-slate-600">Mark as primary contact of this company</label>
              </div>
            </form>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-white">
              <button type="button" onClick={onClose} className="btn-secondary text-xs px-4 py-2">Cancel</button>
              <button type="button" onClick={submit} disabled={saving} className="btn-primary text-xs px-4 py-2" data-testid="contact-drawer-save">
                {saving ? 'Saving…' : 'Create Contact'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
